import React, { useState } from "react";
import Sidebar from "./Sidebar";
import "./manageClassesTests.css";
import { useNavigate } from "react-router-dom";

const ManageClassTests = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  
  // State for managing the create new test modal visibility
  const [showCreateTestModal, setShowCreateTestModal] = useState(false);
  
  // State for storing form data for the new test
  const [newTestData, setNewTestData] = useState({
    testName: "",
    selectedClass: "",
    testType: "weekly",
    date: "",
    duration: "60",
    totalMarks: "100",
    description: ""
  });

  // Class data array with detailed information for each class
  const classes = [
    {
      name: "Play Group",
      tests: 8,
      students: 32,
      completion: 75,
      status: "active",
      color: "pastel-blue",
    },
    {
      name: "Nursery",
      tests: 12,
      students: 28,
      completion: 85,
      status: "active",
      color: "pastel-lavender",
    },
    {
      name: "Prep",
      tests: 15,
      students: 35,
      completion: 92,
      status: "active",
      color: "pastel-mint",
    },
    {
      name: "Class 1",
      tests: 18,
      students: 42,
      completion: 88,
      status: "active",
      color: "pastel-coral",
    },
    {
      name: "Class 2",
      tests: 16,
      students: 38,
      completion: 79,
      status: "warning",
      color: "pastel-peach",
    },
    {
      name: "Class 3",
      tests: 20,
      students: 45,
      completion: 94,
      status: "active",
      color: "pastel-aqua",
    },
    {
      name: "Class 4",
      tests: 14,
      students: 36,
      completion: 82,
      status: "active",
      color: "pastel-lilac",
    },
    {
      name: "Class 5",
      tests: 22,
      students: 48,
      completion: 96,
      status: "excellent",
      color: "pastel-sunshine",
    },
    {
      name: "Class 6",
      tests: 19,
      students: 40,
      completion: 91,
      status: "active",
      color: "pastel-rose",
    },
    {
      name: "Class 7",
      tests: 17,
      students: 39,
      completion: 87,
      status: "active",
      color: "pastel-sky",
    },
    {
      name: "Class 8",
      tests: 24,
      students: 50,
      completion: 98,
      status: "excellent",
      color: "pastel-lime",
    },
    {
      name: "Class Pre 9",
      tests: 13,
      students: 30,
      completion: 68,
      status: "warning",
      color: "pastel-sand",
    },
    {
      name: "Class 9",
      tests: 26,
      students: 52,
      completion: 95,
      status: "active",
      color: "pastel-lavender",
    },
    {
      name: "Class 10",
      tests: 30,
      students: 55,
      completion: 99,
      status: "excellent",
      color: "pastel-mint",
    },
  ];

  // Image filenames corresponding to each class
  const imageFilenames = [
    "pg.png",
    "nur.png",
    "prep.png",
    "1.png",
    "2.png",
    "3.png",
    "4.png",
    "5.png",
    "6.png",
    "7.png",
    "8.png",
    "8.png",
    "9.png",
    "10.png",
  ];

  // Handler for clicking on the "Create New Test" button
  const handleCreateTestClick = () => {
    console.log("Button clicked! Opening modal..."); // Add this
    setShowCreateTestModal(true);
  };

  // Handler for closing the modal
  const handleCloseModal = () => {
    setShowCreateTestModal(false);
    // Reset form data to initial state
    setNewTestData({
      testName: "",
      selectedClass: "",
      testType: "weekly",
      date: "",
      duration: "60",
      totalMarks: "100",
      description: ""
    });
  };

  // Handler for form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTestData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handler for form submission
  const handleSubmitTest = (e) => {
    e.preventDefault();
    
    // Here you would typically send the data to your backend API
    console.log("Creating new test with data:", newTestData);
    
    // Navigate to the Add Test page with the selected class data
    if (newTestData.selectedClass) {
      navigate("/Add-Test", { 
        state: { 
          className: newTestData.selectedClass,
          testData: newTestData
        } 
      });
    }
    
    // Close the modal after submission
    handleCloseModal();
  };

  // Handler for clicking on a class card
  const handleClassClick = (className) => {
    navigate("/Classes-Test-Sheet", { state: { className } });
  };

  // Handler for action buttons on class cards
  const handleActionClick = (action, className, e) => {
    e.stopPropagation(); // Prevent card click event from firing
    switch (action) {
      case "View Tests":
        navigate("/Classes-Test-Sheet", { state: { className } });
        break;
      case "Add Test":
        navigate("/Add-Test", { state: { className } });
        break;
      case "Reports":
        navigate("/Test-Reports", { state: { className } });
        break;
      case "Manage":
        navigate("/Class-Management", { state: { className } });
        break;
      case "TestRecordsScreen":
        navigate("/test-records", { state: { className } });
        break;
      default:
        break;
    }
  };

  // Function to get status color based on status type
  const getStatusColor = (status) => {
    switch (status) {
      case "excellent":
        return "#00b09b"; // Green gradient
      case "active":
        return "#6a11cb"; // Purple gradient
      case "warning":
        return "#f7971e"; // Orange gradient
      default:
        return "#6a11cb"; // Default purple
    }
  };

  // Function to get status label text
  const getStatusLabel = (status) => {
    switch (status) {
      case "excellent":
        return "Excellent";
      case "active":
        return "Active";
      case "warning":
        return "Needs Attention";
      default:
        return "Active";
    }
  };

  // Filter classes based on search query
  const filteredClasses = classes.filter((cls) =>
    cls.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="manage-class-tests-screen">
      <div className="manage-class-tests-dashboard-wrapper">
        {/* Sidebar Component */}
        <Sidebar />
        
        {/* Main Content Area */}
        <main className="manage-class-tests-main-content">
          
          {/* Header Section with Title and Action Buttons */}
          <div className="manage-class-tests-content-header">
            <div className="manage-class-tests-header-left">
              <h1 className="manage-class-tests-main-title">
                Class Tests Management
              </h1>
              <p className="manage-class-tests-subtitle">
                Manage tests, track progress, and generate reports for all classes
              </p>
            </div>
            <div className="manage-class-tests-header-actions">
          
              <button className="manage-class-tests-action-btn manage-class-tests-secondary-btn">
                <span className="manage-class-tests-btn-icon">📁</span>
                <span className="manage-class-tests-btn-text">Bulk Import</span>
              </button>
              <button className="manage-class-tests-action-btn manage-class-tests-success-btn">
                <span className="manage-class-tests-btn-icon">📊</span>
                <span className="manage-class-tests-btn-text">
                  View All Reports
                </span>
              </button>
            </div>
          </div>

          {/* Statistics Overview Cards */}
          <div className="manage-class-tests-stats-overview">
            <div className="manage-class-tests-stat-card manage-class-tests-stat-gradient-purple">
              <div className="manage-class-tests-stat-icon">🏫</div>
              <div className="manage-class-tests-stat-content">
                <div className="manage-class-tests-stat-value">
                  {classes.length}
                </div>
                <div className="manage-class-tests-stat-label">
                  Total Classes
                </div>
              </div>
            </div>
            <div className="manage-class-tests-stat-card manage-class-tests-stat-gradient-blue">
              <div className="manage-class-tests-stat-icon">📝</div>
              <div className="manage-class-tests-stat-content">
                <div className="manage-class-tests-stat-value">
                  {classes.reduce((acc, cls) => acc + cls.tests, 0)}
                </div>
                <div className="manage-class-tests-stat-label">Total Tests</div>
              </div>
            </div>
            <div className="manage-class-tests-stat-card manage-class-tests-stat-gradient-green">
              <div className="manage-class-tests-stat-icon">📊</div>
              <div className="manage-class-tests-stat-content">
                <div className="manage-class-tests-stat-value">
                  {Math.round(
                    classes.reduce((acc, cls) => acc + cls.completion, 0) /
                      classes.length
                  )}
                  %
                </div>
                <div className="manage-class-tests-stat-label">
                  Avg Completion
                </div>
              </div>
            </div>
            <div className="manage-class-tests-stat-card manage-class-tests-stat-gradient-orange">
              <div className="manage-class-tests-stat-icon">⏰</div>
              <div className="manage-class-tests-stat-content">
                <div className="manage-class-tests-stat-value">8</div>
                <div className="manage-class-tests-stat-label">
                  Pending Tests
                </div>
              </div>
            </div>
          </div>

          {/* Search and Filter Section */}
          <div
            className="manage-class-tests-search-filter-section"
            style={{ marginTop: "5vh" }}
          >
            <div className="manage-class-tests-search-container">
              <div className="manage-class-tests-search-wrapper">
                <div className="manage-class-tests-search-icon-wrapper">
                  <svg
                    className="manage-class-tests-search-icon"
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                  >
                    <path
                      fill="currentColor"
                      d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  className="manage-class-tests-search-input"
                  placeholder="Search classes, tests, or students..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    className="manage-class-tests-clear-search"
                    onClick={() => setSearchQuery("")}
                  >
                    ✕
                  </button>
                )}
              </div>
              <div className="manage-class-tests-filter-buttons">
                <button className="manage-class-tests-filter-btn manage-class-tests-filter-btn-active">
                  <span className="manage-class-tests-filter-icon">🏫</span>
                  All Classes
                </button>
                <button className="manage-class-tests-filter-btn">
                  <span className="manage-class-tests-filter-icon">📊</span>
                  High Performance
                </button>
                <button className="manage-class-tests-filter-btn">
                  <span className="manage-class-tests-filter-icon">⚠️</span>
                  Needs Attention
                </button>
                <div className="manage-class-tests-sort-dropdown">
                  <button className="manage-class-tests-sort-btn">
                    <span className="manage-class-tests-sort-icon">↕️</span>
                    Sort By
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Classes Grid Section */}
          <div className="manage-class-tests-grid-header">
            <h2 className="manage-class-tests-section-title">All Classes</h2>
            <div className="manage-class-tests-view-options">
              <span className="manage-class-tests-view-label">View:</span>
              <button className="manage-class-tests-view-option manage-class-tests-view-option-active">
                Grid
              </button>
              <button className="manage-class-tests-view-option">List</button>
            </div>
          </div>

          {/* Classes Grid - Displaying all class cards */}
          <div className="manage-class-tests-grid">
            {filteredClasses.map((cls, index) => (
              <div
                className={`manage-class-tests-card manage-class-tests-card-${cls.color}`}
                key={index}
                onClick={() => handleClassClick(cls.name)}
                data-status={cls.status}
              >
                <div className="manage-class-tests-card-header">
                  <div className="manage-class-tests-image-container">
                    <img
                      src={`/Classes/${imageFilenames[index]}`}
                      alt={cls.name}
                      className="manage-class-tests-image"
                    />
                    <div
                      className="manage-class-tests-badge"
                      style={{ backgroundColor: getStatusColor(cls.status) }}
                    >
                      {getStatusLabel(cls.status)}
                    </div>
                  </div>

                  <div className="manage-class-tests-class-info">
                    <h3 className="manage-class-tests-class-name">
                      {cls.name}
                    </h3>
                    <div className="manage-class-tests-class-meta">
                      <div className="manage-class-tests-meta-item">
                        <span className="manage-class-tests-meta-icon">📊</span>
                        <div className="manage-class-tests-meta-content">
                          <div className="manage-class-tests-meta-value">
                            {cls.tests}
                          </div>
                          <div className="manage-class-tests-meta-label">
                            Tests
                          </div>
                        </div>
                      </div>
                      <div className="manage-class-tests-meta-item">
                        <span className="manage-class-tests-meta-icon">👥</span>
                        <div className="manage-class-tests-meta-content">
                          <div className="manage-class-tests-meta-value">
                            {cls.students}
                          </div>
                          <div className="manage-class-tests-meta-label">
                            Students
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="manage-class-tests-card-body">
                  <div className="manage-class-tests-progress-section">
                    <div className="manage-class-tests-progress-header">
                      <span className="manage-class-tests-progress-label">
                        Test Completion
                      </span>
                      <span className="manage-class-tests-progress-value">
                        {cls.completion}%
                      </span>
                    </div>
                    <div className="manage-class-tests-progress-bar">
                      <div
                        className="manage-class-tests-progress-fill"
                        style={{
                          width: `${cls.completion}%`,
                          background: `linear-gradient(90deg, ${getStatusColor(
                            cls.status
                          )}, ${getStatusColor(cls.status)}80)`,
                        }}
                      ></div>
                    </div>
                    <div className="manage-class-tests-progress-stats">
                      <span className="manage-class-tests-progress-stat">
                        Completed:{" "}
                        {Math.round((cls.tests * cls.completion) / 100)}
                      </span>
                      <span className="manage-class-tests-progress-stat">
                        Pending:{" "}
                        {cls.tests -
                          Math.round((cls.tests * cls.completion) / 100)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="manage-class-tests-card-footer">
                  <div className="manage-class-tests-footer-actions">
                    <button
                      className="manage-class-tests-footer-btn manage-class-tests-footer-view-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleActionClick("View Tests", cls.name, e);
                      }}
                    >
                      <span className="manage-class-tests-footer-btn-text">
                        Add New Test
                      </span>
                      <span className="manage-class-tests-footer-arrow-icon">
                        →
                      </span>
                    </button>
                    <button
                      className="manage-class-tests-footer-btn manage-class-tests-footer-schedule-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleActionClick("TestRecordsScreen", cls.name, e);
                      }}
                    >
                      <span className="manage-class-tests-footer-btn-text">
                        📅 Test Records
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bulk Actions Section */}
          <div className="manage-class-tests-bulk-actions">
            <div className="manage-class-tests-bulk-header">
              <h3 className="manage-class-tests-bulk-title">Bulk Operations</h3>
              <p className="manage-class-tests-bulk-description">
                Perform actions on multiple classes simultaneously
              </p>
            </div>
            <div className="manage-class-tests-bulk-grid">
              <button className="manage-class-tests-bulk-btn">
                <span className="manage-class-tests-bulk-icon">📤</span>
                <span className="manage-class-tests-bulk-label">
                  Export All Data
                </span>
                <span className="manage-class-tests-bulk-subtext">
                  CSV, Excel, PDF
                </span>
              </button>
              <button className="manage-class-tests-bulk-btn">
                <span className="manage-class-tests-bulk-icon">📥</span>
                <span className="manage-class-tests-bulk-label">
                  Import Test Schedule
                </span>
                <span className="manage-class-tests-bulk-subtext">
                  Batch upload
                </span>
              </button>
              <button className="manage-class-tests-bulk-btn">
                <span className="manage-class-tests-bulk-icon">🔄</span>
                <span className="manage-class-tests-bulk-label">
                  Update All Tests
                </span>
                <span className="manage-class-tests-bulk-subtext">
                  Sync changes
                </span>
              </button>
              <button className="manage-class-tests-bulk-btn">
                <span className="manage-class-tests-bulk-icon">📨</span>
                <span className="manage-class-tests-bulk-label">
                  Notify Teachers
                </span>
                <span className="manage-class-tests-bulk-subtext">
                  Email & SMS
                </span>
              </button>
            </div>
          </div>
        </main>
      </div>

   
    </div>
  );
};

export default ManageClassTests;